import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthSessionStorage, JwtPayload } from '@smata-data-access';
import { ROUTE_PATHS } from '@smata-data-shared';
import { NotificationFacade } from '@smata-notification';
import { jwtDecode } from 'jwt-decode';
import { catchError, EMPTY, tap } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { handleApiResponse } from '../utils/api-response-handler';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notificationFacade = inject(NotificationFacade);
  private readonly destroyRef = inject(DestroyRef);

  readonly userName = signal<string | null>(null);
  readonly passcodeError = signal<string | null>(null);

  requestPasscode(identifier: string, isResend: boolean = false): void {
    this.authService
      .requestPasscode(identifier, isResend)
      .pipe(
        tap((res) => {
          const data = handleApiResponse(res, this.notificationFacade);
          if (!data) return;

          AuthSessionStorage.setIdentifier(identifier);
          AuthSessionStorage.setContactType(identifier.includes('@') ? 'email' : 'mobile');
          this.router.navigate([ROUTE_PATHS.passcode], { queryParamsHandling: 'preserve' });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  verifyPasscode(passcode: string): void {
    const identifier = AuthSessionStorage.getIdentifier();
    this.passcodeError.set(null);

    this.authService
      .verifyPasscode(identifier, passcode)
      .pipe(
        tap((res) => {
          const authData = handleApiResponse<string>(res as any, this.notificationFacade);
          if (!authData) return;

          AuthSessionStorage.setToken(authData);
          AuthSessionStorage.removeIdentifier();
          this.router.navigate([ROUTE_PATHS.purchaseOrders], { queryParamsHandling: 'preserve' });
        }),
        catchError((err: unknown) => {
          const message =
            err instanceof Error
              ? err.message
              : err instanceof HttpErrorResponse
                ? err.error.message
                : 'Invalid passcode';

          this.passcodeError.set(message);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onResendPasscode(): void {
    const identifier = AuthSessionStorage.getIdentifier();
    if (!identifier) return;
    this.requestPasscode(identifier, true);
  }

  getCurrentUser(token: string | undefined): void {
    if (!token) {
      this.userName.set(null);
      return;
    }

    const decoded = jwtDecode<JwtPayload>(token);
    this.userName.set(decoded.unique_name ?? null);
  }

  logout(): void {
    this.authService
      .logout()
      .pipe(
        tap((res) => handleApiResponse(res, this.notificationFacade)),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.clearSessionAndKickout());
  }

  refreshTokenBackgroundFlow() {
    return this.authService.refreshToken();
  }

  clearSessionAndKickout(): void {
    AuthSessionStorage.clear();
    this.userName.set(null);
    this.router.navigate([ROUTE_PATHS.login]);
  }
}
